import { useMemo } from "react";
import { toast } from "sonner";
import { useFilledMemoContent } from "@/zapi-frontend/shared/hooks/useFilledMemoContent";
import { chapterMarkdown } from "@/zapi-frontend/shared/lib/chapterMarkdown";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Switch
} from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { ClipboardTextIcon, CopyIcon, DotsThreeIcon, FileMdIcon } from "@phosphor-icons/react";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useEditorStore } from "@/zapi-frontend/stores/editor";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useMemoViewStore } from "@/zapi-frontend/stores/memoView";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";

type TMemoOptionsMenuProps = {
  disabled?: boolean;
};

export function MemoOptionsMenu({ disabled }: TMemoOptionsMenuProps) {
  const { t } = useTranslation("projectHome");
  const { isRawView, setRawView } = useMemoViewStore();
  const { getMarkdown, getHtml, pasteContent } = useEditorStore();
  const { activeChapter } = useChapterStore();
  const { role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const chapterId = activeChapter?.id?.toString() || "";
  const { selectedProject } = useProjectStore();
  const filled = useFilledMemoContent(selectedProject?.id, activeChapter?.activeMemo);
  const figuresByKey = useMemo(
    () => new Map((filled.figures ?? []).map((figure) => [figure.key, figure])),
    [filled.figures]
  );

  const handleCopyMarkdown = async () => {
    try {
      let markdown: string | null = null;
      let html: string | null = null;

      if (activeChapter?.activeMemo?.contentFormat === "blocks") {
        if (filled.content === undefined) {
          toast.error(t("chapterHeader.figuresNotReady"));
          if (filled.isError) filled.refetch();
          return;
        }
        markdown = chapterMarkdown(filled.content, figuresByKey);
      } else if (isRawView) {
        markdown = useChapterStore.getState().getChapterContent(chapterId);
      } else {
        markdown = getMarkdown?.() || null;
        html = getHtml?.() || null;
      }

      if (!markdown) {
        toast.error(t("chapterHeader.noContentToCopy"));
        return;
      }

      // Also expose text/html so pasting back into the editor (⌘V) keeps formatting
      // instead of inserting the raw markdown/HTML as literal text. Fall back to
      // plain-text markdown if the richer ClipboardItem API isn't available/allowed.
      let richCopied = false;
      if (html && typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              "text/plain": new Blob([markdown], { type: "text/plain" }),
              "text/html": new Blob([html], { type: "text/html" })
            })
          ]);
          richCopied = true;
        } catch {
          richCopied = false;
        }
      }
      if (!richCopied) {
        await navigator.clipboard.writeText(markdown);
      }
      toast.success(t("chapterHeader.markdownCopied"));
    } catch (error) {
      console.error("Failed to copy markdown:", error);
      toast.error(t("chapterHeader.copyFailed"));
    }
  };

  const handlePasteMarkdown = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      if (!clipboardText.trim()) {
        toast.error(t("chapterHeader.noContentInClipboard"));
        return;
      }

      if (isRawView) {
        useChapterStore.getState().updateChapterContent(chapterId, clipboardText, true);
      } else {
        if (!pasteContent) {
          toast.error(t("chapterHeader.editorNotReady"));
          return;
        }
        pasteContent(clipboardText);
      }

      toast.success(t("chapterHeader.markdownPasted"));
    } catch (error) {
      console.error("Failed to paste markdown:", error);
      toast.error(t("chapterHeader.pasteFailed"));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild data-testid="memo-options-trigger">
        <AppButton
          appearance="contained"
          color="zinc100"
          size="icon"
          title={t("chapterHeader.moreOptions")}
          disabled={disabled}
          startAdornment={<DotsThreeIcon />}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[225px] p-1 border-innoveraZinc-100">
        <div
          className="flex items-center justify-between gap-3 rounded-lg px-3 py-2"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1.5">
            <FileMdIcon color="var(--innovera-zinc900)" />
            <FigmaTypography variant="label-2">{t("chapterHeader.markdown")}</FigmaTypography>
          </div>
          <Switch
            checked={isRawView}
            onCheckedChange={setRawView}
            className="h-5 w-8"
            data-testid="memo-options-markdown-toggle"
          />
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="memo-options-copy"
          onClick={handleCopyMarkdown}
          className="px-3 py-2 flex items-center gap-1.5"
        >
          <CopyIcon color="var(--innovera-zinc900)" />
          <FigmaTypography variant="label-2">{t("chapterHeader.copy")}</FigmaTypography>
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="memo-options-paste"
          onClick={handlePasteMarkdown}
          disabled={!hasEdit}
          className="px-3 py-2 flex items-center gap-1.5"
        >
          <ClipboardTextIcon color="var(--innovera-zinc900)" />
          <FigmaTypography variant="label-2">{t("chapterHeader.paste")}</FigmaTypography>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
